import { faArchive, faChevronLeft, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { memo, useCallback, useRef, useState } from 'react'
import { PulseLoader } from 'react-spinners'
import styled, { keyframes } from 'styled-components'
import theme from '~/design'
import { Chat } from './chatCard'
import ChatListGrouped from './chatListGrouped'
import UserAvatar from './userAvatar'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

const FadeInContainer = styled.div`
  animation: ${fadeIn} 0.3s ease forwards;
  height: 100%;
`

const SidebarContainer = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  box-sizing: border-box;
`

const HeaderSidebar = styled.div`
  padding: 15px 15px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ListButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const NewChatButton = styled.button`
  border: none;
  padding: 10px 15px;
  margin: 0;
  font-size: 14px;
  background: none;
  cursor: pointer;
  color: white;
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  transition: opacity 0.2s;
  outline: none;

  &:hover {
    opacity: 0.7;
  }
`

const Button = styled(NewChatButton)`
  color: ${({ theme }) => theme.colors.darkGrey};
  background: none;
  transition: background 0.2s;

  &:hover {
    opacity: 1;
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const IconSize = styled.div`
  font-size: 20px;
  font-weight: normal;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`

const Hr = styled.hr`
  padding: 0 10px;
  border: none;
  border-top: 1px solid ${theme.colors.lightGrey};
  height: 1px;
  width: 100%;
  box-sizing: border-box;
`

const ChatListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0 15px;
  padding-bottom: 50px;
`

const ChatListLoading = styled.div`
  padding: 10px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const ArchivedHeaderTitle = styled(NewChatButton)`
  cursor: default;

  &:hover {
    opacity: 1;
  }
`

interface SidebarProps {
  chats: Chat[]
  currentChatId?: number
  listType: 'active' | 'archived'
  hasMoreChats?: boolean
  onScrollChatListToBottom?: (callback: () => void) => void
  loading?: boolean
  onChangeListType?: (type: 'active' | 'archived') => void
  openSearchModal?: () => void
}

const Sidebar: React.FC<SidebarProps> = ({
  chats,
  currentChatId,
  listType,
  hasMoreChats = false,
  onScrollChatListToBottom,
  loading = false,
  onChangeListType,
  openSearchModal,
}) => {
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState<boolean>(false)
  const endScrollWasReached = useRef<boolean>(false)

  const handleScrollChatList = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (
        e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight <=
          2 &&
        hasMoreChats &&
        !endScrollWasReached.current &&
        onScrollChatListToBottom
      ) {
        setIsLoadingMoreChats(true)
        endScrollWasReached.current = true
        onScrollChatListToBottom?.(() => {
          setIsLoadingMoreChats(false)
          endScrollWasReached.current = false
        })
      }
    },
    [hasMoreChats, onScrollChatListToBottom, endScrollWasReached],
  )

  const handleNewChat = useCallback(() => {
    onChangeListType?.('active')
  }, [onChangeListType])

  return (
    <FadeInContainer>
      <SidebarContainer>
        <HeaderSidebar>
          <ListButtons>
            {listType === 'archived' ? (
              <>
                <ArchivedHeaderTitle key={'archived-llm-sidebar'}>
                  <FontAwesomeIcon icon={faArchive} />
                  Arquivados
                </ArchivedHeaderTitle>

                <Button key={'search-llm-sidebar'} onClick={openSearchModal}>
                  <FontAwesomeIcon icon={faSearch} />
                  Buscar chat
                </Button>

                <Button
                  key={'back-archived-llm-sidebar'}
                  className='back-archived-llm-sidebar'
                  onClick={() => {
                    onChangeListType?.('active')
                  }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Voltar para chats ativos
                </Button>
              </>
            ) : (
              <>
                <NewChatButton key={'new-chat-llm-sidebar'} onClick={handleNewChat}>
                  <FontAwesomeIcon icon={faPlus} />
                  Novo chat
                </NewChatButton>
                <Button key={'search-llm-sidebar'} onClick={openSearchModal}>
                  <FontAwesomeIcon icon={faSearch} />
                  Buscar chat
                </Button>
              </>
            )}
          </ListButtons>

          <Hr />
        </HeaderSidebar>

        <ChatListContainer onScroll={handleScrollChatList}>
          <ChatListGrouped
            chats={chats}
            currentChatId={currentChatId}
            loading={loading}
            listType={listType}
          />

          {isLoadingMoreChats && (
            <ChatListLoading>
              <PulseLoader size={8} color='#666' speedMultiplier={0.7} />
            </ChatListLoading>
          )}
        </ChatListContainer>

        <UserAvatar
          onClickArchived={() => {
            if (listType === 'active') {
              onChangeListType?.('archived')
            }
          }}
        />
      </SidebarContainer>
    </FadeInContainer>
  )
}

export default memo(Sidebar)
